import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import "./HeroSection.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarDays } from "@fortawesome/free-solid-svg-icons";

const HeroSkeleton = () => (
  <div className="hero-section">
    <h1 className="hero-h1">Top News</h1>
    <div className="hero-container hero-skel-grid" aria-hidden="true">
      <div className="hero-skel-side">
        <div className="hero-skel-card" />
        <div className="hero-skel-card" />
      </div>
      <div className="hero-skel-center">
        <div className="hero-skel-card hero-skel-card--center" />
      </div>
      <div className="hero-skel-side">
        <div className="hero-skel-card" />
        <div className="hero-skel-card" />
      </div>
      <div className="hero-skel-rail">
        <div className="hero-skel-rail-card" />
        <div className="hero-skel-rail-card" />
        <div className="hero-skel-rail-card" />
        <div className="hero-skel-rail-card" />
      </div>
    </div>
  </div>
);

// isLcp  → LCP element: fetchpriority=high, eager
// eager  → in-viewport side images: eager, fetchpriority=auto
const HeroImg = ({ src, alt, isLcp, eager }) => {
  const [loadKey, setLoadKey] = useState(0);
  const [broken, setBroken] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const retries = useRef(0);

  const handleError = () => {
    if (!src || retries.current >= 2) {
      setBroken(true);
      return;
    }
    retries.current += 1;
    setTimeout(() => {
      setLoaded(false);
      setLoadKey((k) => k + 1);
    }, 800 * retries.current);
  };

  // No URL or permanent failure — neutral placeholder, no broken-image icon
  if (!src || broken) {
    return <div className="hero-img-shell" aria-hidden="true" />;
  }

  return (
    <div className="hero-img-shell">
      {!loaded && <div className="hero-img-skel" aria-hidden="true" />}
      <img
        key={loadKey}
        src={src}
        alt={alt || "Article image"}
        style={{ opacity: loaded ? 1 : 0 }}
        loading={isLcp || eager ? "eager" : "lazy"}
        fetchpriority={isLcp ? "high" : "auto"}
        decoding="async"
        onLoad={() => setLoaded(true)}
        onError={handleError}
      />
    </div>
  );
};

// Read server-injected data synchronously — no network wait on first render
function getInitialArticles() {
  try {
    const data = window.__HERO_ARTICLES__;
    if (Array.isArray(data) && data.length > 0) return data.filter((a) => a.image_url);
  } catch (_) {}
  return [];
}

const HeroSection = () => {
  const initial = getInitialArticles();
  const [featuredArticles, setFeaturedArticles] = useState(initial);
  const [status, setStatus] = useState(initial.length ? "ok" : "loading");

  useEffect(() => {
    // Background refresh — keep showing existing data while fetch runs
    axios
      .get("/api/articles/featured")
      .then((res) => {
        const payload = res?.data;
        const raw = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload?.articles)
          ? payload.articles
          : [];
        // Only show articles that actually have an image
        const list = raw.filter((a) => a.image_url);
        if (list.length) {
          setFeaturedArticles(list);
          setStatus("ok");
        } else if (status !== "ok") {
          setStatus("empty");
        }
      })
      .catch((e) => {
        console.error("Error fetching featured articles:", e);
        if (status !== "ok") setStatus("error");
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (status !== "ok") return <HeroSkeleton />;

  const items = featuredArticles;
  const centerArticle = items[0];
  const leftSideArticles = items.slice(1, 3);
  const rightSideArticles = items.slice(3, 5);
  const mobileSideArticles = items.slice(1, 5);

  if (!centerArticle) return <HeroSkeleton />;

  const fmtDate = (d) =>
    new Date(d || Date.now()).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });

  return (
    <div className="hero-section">
      <h1 className="hero-h1">Top News</h1>

      <div className="hero-container">
        <div className="side-articles">
          {leftSideArticles.map((a) => (
            <div key={a.id} className="side-article">
              <a href={`/articles/${a.slug}`}>
                <HeroImg src={a.image_url} alt={a.title} eager />
              </a>
              <h3>
                <a href={`/articles/${a.slug}`}>{a.title}</a>
              </h3>
              <div className="date-container">
                <FontAwesomeIcon icon={faCalendarDays} className="calendar-icon" />
                <span className="featured-date">{fmtDate(a.created_at)}</span>
              </div>
              <a href={`/articles/${a.slug}`}>
                <p
                  className="hero-article-content"
                  dangerouslySetInnerHTML={{ __html: a.excerpt || "" }}
                />
              </a>
            </div>
          ))}
        </div>

        <div className="center-article">
          <a href={`/articles/${centerArticle.slug}`}>
            <HeroImg src={centerArticle.image_url} alt={centerArticle.title} isLcp />
          </a>
          <div className="featured-content">
            <h1>
              <a href={`/articles/${centerArticle.slug}`}>{centerArticle.title}</a>
            </h1>
            <a href={`/articles/${centerArticle.slug}`}>
              <div className="date-container">
                <FontAwesomeIcon icon={faCalendarDays} className="calendar-icon" />
                <span className="featured-date">{fmtDate(centerArticle.created_at)}</span>
              </div>
              <p
                className="center-article-content"
                dangerouslySetInnerHTML={{ __html: centerArticle.excerpt || "" }}
              />
            </a>
          </div>
        </div>

        <div className="side-articles">
          {rightSideArticles.map((a) => (
            <div key={a.id} className="side-article">
              <a href={`/articles/${a.slug}`}>
                <HeroImg src={a.image_url} alt={a.title} eager />
              </a>
              <h3>
                <a href={`/articles/${a.slug}`}>{a.title}</a>
              </h3>
              <div className="date-container">
                <FontAwesomeIcon icon={faCalendarDays} className="calendar-icon" />
                <span className="featured-date">{fmtDate(a.created_at)}</span>
              </div>
              <a href={`/articles/${a.slug}`}>
                <p
                  className="hero-article-content"
                  dangerouslySetInnerHTML={{ __html: a.excerpt || "" }}
                />
              </a>
            </div>
          ))}
        </div>

        <div className="mobile-rail" aria-label="More top news">
          {mobileSideArticles.map((a) => (
            <article key={a.id} className="mobile-card">
              <a href={`/articles/${a.slug}`}>
                <HeroImg src={a.image_url} alt={a.title} eager />
              </a>
              <h3>
                <a href={`/articles/${a.slug}`}>{a.title}</a>
              </h3>
              <div className="date-container">
                <FontAwesomeIcon icon={faCalendarDays} className="calendar-icon" />
                <span className="featured-date">{fmtDate(a.created_at)}</span>
              </div>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
};

export default HeroSection;
